import DSAuth from './DSAuth';
import authWebviewHandler from './AuthWebViewHandler';
import { AuthChoice } from './data/AuthChoice';
import { GenericRequest, HTTPFetchResponse, PrimaryTokenStatus } from './data/AuthTypes';
import { stringConstants, primaryTokenStatusConstants } from '../AuthConstants';
import { WebviewAuthenticateResponse } from './data/WebviewAuthenticateResponse';
import Utils from '../../utils/Utils';
import DsAuthPrimaryTokenServiceBase from './DsAuthPrimaryTokenServiceBase';
import { WebviewAuthResponseParser } from './parser/WebviewAuthResponseParser';
import ExplicitFormsHelper from './ExplicitFormsHelper';
import { config } from '../AuthConfig';
import { RequestTokenChoicesParser } from './parser/RequestTokenChoicesParser';
import { Logger } from '../Logger';
import { logConstants } from '../../utils/Constants';
import { SentryLogger } from '../../sentryLogging/SentryLogger';
import { PrimaryTokenFetchFailure } from '../AuthErrors';
import FormsAuthenticator from './Authenticators/FormsAuthenticator';
import { AuthenticateError, isTokenResponse } from './Authenticators/Authenticator';

interface StartAndPostBackUrls {
	readonly startUrl: string;
	readonly postBackUrl: string;
}

export default class CloudPrimaryTokenService extends DsAuthPrimaryTokenServiceBase {
	private readonly protocol = 'ExplicitForms';

	private static instance: CloudPrimaryTokenService;
	static getInstance(): CloudPrimaryTokenService {
		if (!CloudPrimaryTokenService.instance) {
			CloudPrimaryTokenService.instance = new CloudPrimaryTokenService();
		}
		return CloudPrimaryTokenService.instance;
	}

	protected async getToken(
		authProtocolsResponse: HTTPFetchResponse,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<PrimaryTokenStatus> {
		if (config.CredentialInsertionEnabled) {
			const authenticator = new FormsAuthenticator();
			const authChoice = this.getAuthChoice(authProtocolsResponse, this.protocol, id);

			const primaryTokenResponse = await authenticator.getToken(
				authChoice,
				authProtocolsRequest,
				id,
				referenceUri,
				transactionId
			);

			if (!isTokenResponse(primaryTokenResponse)) {
				if (primaryTokenResponse === AuthenticateError.CancelledByUser) {
					return new PrimaryTokenStatus(
						primaryTokenStatusConstants.ERROR_CANCEL_LOGIN,
						true
					);
				}

				throw new PrimaryTokenFetchFailure(new Error('Unexpected AuthenticateError'));
			} else {
				return await this.validateAndStoreToken(
					primaryTokenResponse,
					referenceUri,
					id,
					'login',
					true
				);
			}
		} else {
			const { startUrl, postBackUrl } = await this.getStartUrl(
				id,
				referenceUri,
				this.getAuthChoice(authProtocolsResponse, this.protocol, id),
				authProtocolsRequest
			);

			const startUrlWithParams = this.addNrAndRtQueryParamsIfMissing(
				startUrl,
				referenceUri
			);

			// open webview for authentication
			const webviewResponse = await authWebviewHandler.start(
				startUrlWithParams,
				referenceUri,
				id,
				stringConstants.AUTHWEBVIEW,
				transactionId
			);

			// process response and decide whether to get primary token
			return await this.processWebViewResponseAndGetToken(
				webviewResponse,
				postBackUrl,
				referenceUri,
				id,
				transactionId
			);
		}
	}

	protected getPrimaryTokenLifetime(): string {
		return config.primaryTokenLifetime;
	}

	private getAuthChoice(
		authProtocolsResponse: HTTPFetchResponse,
		protocol: string,
		id: string
	): AuthChoice {
		try {
			const requestTokenChoicesParser = new RequestTokenChoicesParser();
			const authChoice = requestTokenChoicesParser.getAuthChoice(
				authProtocolsResponse.body,
				protocol
			);
			Logger.getInstance().write(
				logConstants.INFO,
				id + ': Auth choice: ' + JSON.stringify(authChoice)
			);

			return authChoice;
		} catch (e) {
			SentryLogger.getInstance().logError(e, {
				customMessage:
					'Processing auth protocols response failed in CloudPrimaryTokenService',
				additionalContext: {
					innerError: e.innerError,
				},
			});
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: DSAUTH_PROCESS_AUTHPROTOCOLS_RESPONSE_FAILURE in CloudPrimaryTokenService: ${e.message}`
			);

			throw new PrimaryTokenFetchFailure(e);
		}
	}

	private async getStartUrl(
		id: string,
		referenceUri: string,
		authProtocol: AuthChoice,
		authProtocolsRequest: GenericRequest
	): Promise<StartAndPostBackUrls> {
		// fetch primary token using explicit forms
		const explicitFormsLocation = authProtocol.getLocation();
		const explicitFormsHelper = new ExplicitFormsHelper(
			WebviewAuthResponseParser.getInstance()
		);
		const explicitFormsResponse: WebviewAuthenticateResponse =
			(await explicitFormsHelper.getExplicitFormsRequestResponse(
				authProtocolsRequest,
				explicitFormsLocation,
				referenceUri,
				id,
				false
			)) as WebviewAuthenticateResponse;
		return {
			startUrl: explicitFormsResponse.getWebViewStartUrl(),
			postBackUrl:
				Utils.getInstance().getOrigin(explicitFormsLocation) +
				explicitFormsResponse.getPostBack(),
		};
	}

	private addNrAndRtQueryParamsIfMissing(startUrl: string, referenceUri: string): string {
		const url = new URL(startUrl, window.location.origin);

		if (!url.searchParams.has('nr')) {
			url.searchParams.set('nr', '1');
		}
		if (!url.searchParams.has('_rt')) {
			url.searchParams.set('_rt', referenceUri);
		}

		return url.toString();
	}

	private async processWebViewResponseAndGetToken(
		webviewResponse: string,
		postBackUrl: string,
		referenceUri: string,
		id: string,
		transactionId: string
	): Promise<PrimaryTokenStatus> {
		if (webviewResponse === primaryTokenStatusConstants.ERROR_CANCEL_LOGIN) {
			return new PrimaryTokenStatus(webviewResponse, true);
		} else {
			const primaryTokenRequest = this.preparePrimaryTokenRequest(
				webviewResponse,
				postBackUrl,
				transactionId
			);
			const primaryTokenResponse = await this.fetchToken(
				primaryTokenRequest,
				referenceUri,
				id
			);
			return await this.validateAndStoreToken(
				primaryTokenResponse,
				referenceUri,
				id,
				'login'
			);
		}
	}

	private preparePrimaryTokenRequest(
		webviewResponse: string,
		postBackUrl: string,
		transactionId: string
	): GenericRequest {
		const headers = DSAuth.getInstance().addBasicHeaders(
			{
				Accept: '*/*',
				'Content-Type': 'application/x-www-form-urlencoded',
				'Cache-Control': 'no-cache',
			},
			transactionId
		);
		return {
			method: stringConstants.POST,
			headers,
			body: 'StateContext=&webviewResponseId=' + webviewResponse,
			url: postBackUrl,
		};
	}
}
